<section class="managing-consumer-page">
  <div class="page-shell">
    <header class="page-header app-card">
      <div>
        <h1>{{ title() }}</h1>
        <span>{{ description() }}</span>
      </div>
    </header>

    <section class="stats-grid" aria-label="Consumer summary">
      @for (card of stats(); track card.label) {
        <article class="stat-card app-card" [class]="'tone-' + card.tone">
          <div class="stat-icon" aria-hidden="true">{{ card.icon }}</div>
          <div class="stat-copy">
            <span>{{ card.label }}</span>
            <strong>
              {{ card.value }}
              @if (card.label === 'Contracted Capacity') {
                <small>MW</small>
              }
            </strong>
          </div>
        </article>
      }
    </section>

    <section class="table-card app-card">
      <div class="filter-grid">
        <label class="filter-field filter-field--wide">
          <span>Search Consumer</span>
          <input
            class="app-form-input"
            type="search"
            placeholder="Search consumer code, name, utility, or contact"
            [value]="searchTerm()"
            (input)="updateSearch($any($event.target).value)"
          >
        </label>

        <label class="filter-field">
          <span>Consumer Type</span>
          <select class="app-form-select" [value]="selectedConsumerType()" (change)="updateConsumerType($any($event.target).value)">
            <option value="All">All</option>
            <option value="Open Access">Open Access</option>
            <option value="Captive">Captive</option>
          </select>
        </label>

        <label class="filter-field">
          <span>Status</span>
          <select class="app-form-select" [value]="selectedStatus()" (change)="updateStatus($any($event.target).value)">
            <option value="All">All</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
        </label>

        <div class="filter-actions">
          <button type="button" class="app-button app-button--primary">Search</button>
          <button type="button" class="app-button app-button--secondary" (click)="resetFilters()">Reset</button>
        </div>
      </div>

      <div class="table-wrap">
        <table class="consumer-table">
          <thead>
            <tr>
              <th>Consumer Code</th>
              <th>Consumer Name</th>
              <th>Consumer Type</th>
              <th>Linked Utility</th>
              <th>Contact Person</th>
              <th>Mobile</th>
              <th>Mapped Units</th>
              <th>Mapped PSS</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            @for (row of paginatedRows(); track row.consumer_id) {
              <tr>
                <td>{{ row.code }}</td>
                <td>{{ row.name }}</td>
                <td>{{ row.consumer_type }}</td>
                <td>{{ row.utility_name }}</td>
                <td>{{ row.contact_person_name }}</td>
                <td>{{ row.mobile_number }}</td>
                <td>{{ row.mapped_units }}</td>
                <td>{{ row.mapped_pss }}</td>
                <td>
                  <span class="status-pill" [class.inactive]="row.status === 'Inactive'">{{ row.status }}</span>
                </td>
                <td>
                  <button type="button" class="manage-button" (click)="openMapUnitsModal(row.consumer_id)">Map Units</button>
                </td>
              </tr>
            } @empty {
              <tr>
                <td colspan="10" class="empty-state">No consumer records found for the selected filters.</td>
              </tr>
            }
          </tbody>
        </table>
      </div>

      <footer class="table-footer">
        <span>{{ showingText() }}</span>

        <div class="pagination">
          <button type="button" class="page-button" [disabled]="currentPage() === 1" (click)="goToPage(currentPage() - 1)">‹</button>
          @for (page of pageNumbers(); track page) {
            <button type="button" class="page-button" [class.active]="page === currentPage()" (click)="goToPage(page)">
              {{ page }}
            </button>
          }
          <button type="button" class="page-button" [disabled]="currentPage() === totalPages()" (click)="goToPage(currentPage() + 1)">›</button>
        </div>
      </footer>
    </section>
  </div>

  @if (isMapUnitsModalOpen() && selectedConsumer()) {
    <div class="modal-backdrop" role="presentation">
      <div class="manage-mapping-modal app-card" role="dialog" aria-modal="true" aria-labelledby="manage-consumer-mapping-title">
        <div class="modal-header">
          <div>
            <h2 id="manage-consumer-mapping-title">Map Units for {{ selectedConsumer()?.name }}</h2>
          </div>
          <button type="button" class="modal-close" (click)="closeMapUnitsModal()" aria-label="Close consumer mapping modal">×</button>
        </div>

        <div class="modal-tabs">
          <button type="button" class="tab-button" [class.active]="activeTab() === 'available'" (click)="switchTab('available')">
            Available Units
          </button>
          <button type="button" class="tab-button" [class.active]="activeTab() === 'mapped'" (click)="switchTab('mapped')">
            Mapped Units ({{ mappedUnits().length }})
          </button>
        </div>

        <div class="modal-body">
          @if (activeTab() === 'available') {
            <div class="modal-toolbar">
              <label class="modal-search">
                <input
                  class="app-form-input"
                  type="search"
                  placeholder="Search available units..."
                  [value]="availableUnitSearch()"
                  (input)="updateAvailableUnitSearch($any($event.target).value)"
                >
              </label>

              <button type="button" class="add-selected-button" [disabled]="selectedAvailableUnitCodes().length === 0" (click)="addSelectedUnits()">
                Add Selected
              </button>
            </div>

            <div class="modal-table-wrap">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>PSS</th>
                    <th>Generating Company</th>
                    <th>Unit</th>
                    <th>Installed Capacity (MW)</th>
                  </tr>
                </thead>
                <tbody>
                  @for (unit of filteredAvailableUnits(); track unit.code) {
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          [checked]="selectedAvailableUnitCodes().includes(unit.code)"
                          (change)="toggleAvailableSelection(unit.code, $any($event.target).checked)"
                        >
                      </td>
                      <td>{{ unit.pss_name }}</td>
                      <td>{{ unit.generating_company_name }}</td>
                      <td>{{ unit.name }}</td>
                      <td>{{ unit.installed_capacity_mw }}</td>
                    </tr>
                  } @empty {
                    <tr>
                      <td colspan="5" class="empty-state">No available units found for this consumer's utility.</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          } @else {
            <div class="modal-toolbar">
              <label class="modal-search">
                <input
                  class="app-form-input"
                  type="search"
                  placeholder="Search mapped units..."
                  [value]="mappedUnitSearch()"
                  (input)="updateMappedUnitSearch($any($event.target).value)"
                >
              </label>

              <button type="button" class="remove-selected-button" [disabled]="selectedMappedUnitCodes().length === 0" (click)="removeSelectedUnits()">
                Remove Selected
              </button>
            </div>

            <div class="modal-table-wrap">
              <table class="modal-table">
                <thead>
                  <tr>
                    <th></th>
                    <th>PSS</th>
                    <th>Generating Company</th>
                    <th>Unit</th>
                    <th>Contracted Capacity (MW)</th>
                    <th>Effective From</th>
                    <th>Effective To</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  @for (unit of filteredMappedUnits(); track unit.id) {
                    <tr>
                      <td>
                        <input
                          type="checkbox"
                          [checked]="selectedMappedUnitCodes().includes(unit.generating_unit_code)"
                          (change)="toggleMappedSelection(unit.generating_unit_code, $any($event.target).checked)"
                        >
                      </td>
                      <td>{{ unit.pss_name }}</td>
                      <td>{{ unit.generating_company_name }}</td>
                      <td>{{ unit.generating_unit_name }}</td>
                      <td>{{ unit.contracted_capacity_mw }}</td>
                      <td>{{ formatDate(unit.effective_from) }}</td>
                      <td>{{ formatDate(unit.effective_to) }}</td>
                      <td>
                        <button type="button" class="remove-icon-button" (click)="removeMappedUnit(unit.generating_unit_code)" aria-label="Remove mapped unit">🗑</button>
                      </td>
                    </tr>
                  } @empty {
                    <tr>
                      <td colspan="8" class="empty-state">No mapped units available for this consumer.</td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>
      </div>
    </div>
  }
</section>
